/*
 * Admin Dashboard (iter326) — Property inventory intelligence, admin-only.
 * Read-only. Reuses the Reports → Dashboard searchable-multi-select pattern.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, X, Search, Shield, LayoutDashboard, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Compact searchable multi-select with counts + Select All / Clear.
const FilterMultiSelect = ({ label, options, value, onChange, testid, headerCount }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(o => String(o.value).toLowerCase().includes(needle));
  }, [q, options]);

  const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  const selectAll = () => onChange(Array.from(new Set([...value, ...filtered.map(o => o.value)])));

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el && !el.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, testid]);

  const badge = value.length > 0
    ? `${value.length} selected`
    : (headerCount != null ? `All (${headerCount.toLocaleString()})` : 'All');

  return (
    <div className="relative flex-1 min-w-[180px]" data-testid={testid}>
      <label className="text-[11px] text-gray-500 uppercase tracking-wide mb-1 block">
        {label}{headerCount != null && <span className="ml-1 text-gray-400 normal-case">({headerCount.toLocaleString()})</span>}
      </label>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 flex items-center justify-between text-left"
        data-testid={`${testid}-button`}>
        <span className="truncate text-gray-700">{badge}</span>
        <svg className="w-3 h-3 text-gray-400" viewBox="0 0 12 12"><path d="M2 4l4 4 4-4" stroke="currentColor" fill="none" strokeWidth="1.5"/></svg>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-2 border-b border-gray-100">
            <span className="text-[11px] font-bold text-gray-500 uppercase">{label}</span>
            <div className="flex items-center gap-2">
              <button onClick={selectAll} className="text-[10px] text-teal-600 hover:underline" data-testid={`${testid}-select-all`}>Select All</button>
              {value.length > 0 && (
                <button onClick={() => onChange([])} className="text-[10px] text-teal-600 hover:underline" data-testid={`${testid}-clear`}>Clear</button>
              )}
            </div>
          </div>
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded border border-gray-200">
              <Search size={11} className="text-gray-400"/>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="flex-1 bg-transparent outline-none text-xs"
                data-testid={`${testid}-search`}
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && <div className="p-3 text-xs text-gray-400">No options</div>}
            {filtered.map(opt => (
              <label key={opt.value} className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-xs" data-testid={`${testid}-opt-${opt.value}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <input type="checkbox" checked={value.includes(opt.value)} onChange={() => toggle(opt.value)} />
                  <span className="truncate">{opt.value}</span>
                </span>
                <span className="text-[10px] text-gray-400 tabular-nums shrink-0">({opt.count.toLocaleString()})</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin' ||
    (Array.isArray(user?.roles) && user.roles.some(r => String(r).toLowerCase() === 'admin'));

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [rateRanges, setRateRanges] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomCounts, setRoomCounts] = useState([]);
  const [globalSearch, setGlobalSearch] = useState('');
  // iter327 — Date filter (matches Reports Dashboard: All / Custom / Week / Monthly).
  const [dateMode, setDateMode] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pendingStart, setPendingStart] = useState('');
  const [pendingEnd, setPendingEnd] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // iter327 — Resolve week / monthly / custom → concrete start/end
  // (frontend authoritative, same convention as Reports Dashboard).
  const effectiveRange = useMemo(() => {
    if (dateMode === 'week') {
      const now = new Date();
      const day = now.getDay();
      const monday = new Date(now); monday.setDate(now.getDate() - ((day + 6) % 7));
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      return { start: monday.toISOString().slice(0, 10), end: sunday.toISOString().slice(0, 10) };
    }
    if (dateMode === 'monthly') {
      const now = new Date();
      const y = now.getFullYear(); const m = now.getMonth();
      const start = new Date(y, m, 1); const end = new Date(y, m + 1, 0);
      return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
    }
    if (dateMode === 'custom') return { start: startDate || null, end: endDate || null };
    return { start: null, end: null };
  }, [dateMode, startDate, endDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (countries.length) p.set('countries', countries.join('|'));
      if (states.length) p.set('states', states.join('|'));
      if (cities.length) p.set('cities', cities.join('|'));
      if (rateRanges.length) p.set('rate_ranges', rateRanges.join('|'));
      if (roomTypes.length) p.set('room_types', roomTypes.join('|'));
      if (roomCounts.length) p.set('room_counts', roomCounts.join('|'));
      if (globalSearch.trim()) p.set('q', globalSearch.trim());
      p.set('date_mode', dateMode);
      if (effectiveRange.start) p.set('start_date', effectiveRange.start);
      if (effectiveRange.end) p.set('end_date', effectiveRange.end);
      p.set('page', String(page));
      p.set('page_size', String(pageSize));
      const r = await fetch(`${API_BASE}/api/admin-dashboard?${p.toString()}`, { headers: authHeader() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      toast.error('Admin Dashboard load failed');
    } finally {
      setLoading(false);
    }
  }, [countries, states, cities, rateRanges, roomTypes, roomCounts, globalSearch, dateMode, effectiveRange, page, pageSize]);

  useEffect(() => { if (isAdmin) fetchData(); }, [isAdmin, fetchData]);
  // Reset page when any filter changes.
  useEffect(() => { setPage(1); }, [countries, states, cities, rateRanges, roomTypes, roomCounts, globalSearch, dateMode, startDate, endDate]);

  const clearAll = () => {
    setCountries([]); setStates([]); setCities([]);
    setRateRanges([]); setRoomTypes([]); setRoomCounts([]);
    setGlobalSearch('');
    setDateMode('all'); setStartDate(''); setEndDate(''); setPendingStart(''); setPendingEnd('');
  };

  const totalPages = data ? Math.max(1, data.total_pages) : 1;

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="admin-dashboard-forbidden">
        <Shield className="mx-auto mb-3 text-gray-300" size={40}/>
        <p className="text-sm">Admin Dashboard is available to Admins only.</p>
      </div>
    );
  }

  const fmtRate = (v) => (v === null || v === undefined) ? '—' : `₹${Number(v).toLocaleString()}`;

  // Selected chips flattened.
  const _dateChipLabel = dateMode === 'week' ? 'Week'
    : dateMode === 'monthly' ? 'Monthly'
    : (dateMode === 'custom' && startDate && endDate) ? `${startDate} → ${endDate}`
    : null;
  const chips = [
    ...(_dateChipLabel ? [{ label: `Date: ${_dateChipLabel}`, key: 'date', clear: () => { setDateMode('all'); setStartDate(''); setEndDate(''); setPendingStart(''); setPendingEnd(''); } }] : []),
    ...countries.map(v => ({ label: v, key: 'country', clear: () => setCountries(countries.filter(x => x !== v)) })),
    ...states.map(v => ({ label: v, key: 'state', clear: () => setStates(states.filter(x => x !== v)) })),
    ...cities.map(v => ({ label: v, key: 'city', clear: () => setCities(cities.filter(x => x !== v)) })),
    ...rateRanges.map(v => ({ label: v, key: 'rate', clear: () => setRateRanges(rateRanges.filter(x => x !== v)) })),
    ...roomTypes.map(v => ({ label: v, key: 'type', clear: () => setRoomTypes(roomTypes.filter(x => x !== v)) })),
    ...roomCounts.map(v => ({ label: v, key: 'count', clear: () => setRoomCounts(roomCounts.filter(x => x !== v)) })),
  ];

  const hc = data?.header_counts || {};

  return (
    <div className="space-y-5" data-testid="admin-dashboard-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LayoutDashboard className="text-teal-600" size={26}/> Admin Dashboard
            <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200" data-testid="admin-only-badge">Admin Only</span>
          </h1>
          <p className="text-xs text-gray-500 mt-1">View properties across countries, states, cities, room types and rates.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 px-2 py-1 bg-white border border-gray-300 rounded-lg">
            <Search size={12} className="text-gray-400"/>
            <input
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              placeholder="Search property..."
              className="bg-transparent outline-none text-xs w-48"
              data-testid="admin-global-search"
            />
          </div>
          {/* iter327 — Date filter (top-right, same UX as Reports Dashboard). */}
          {dateMode === 'custom' && (
            <>
              <input type="date" value={pendingStart} onChange={(e) => setPendingStart(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs" data-testid="admin-start-date"/>
              <span className="text-xs text-gray-400">to</span>
              <input type="date" value={pendingEnd} onChange={(e) => setPendingEnd(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs" data-testid="admin-end-date"/>
              <button
                onClick={() => { setStartDate(pendingStart); setEndDate(pendingEnd); }}
                disabled={!pendingStart || !pendingEnd}
                className="px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 disabled:opacity-60"
                data-testid="admin-apply-date">
                Apply
              </button>
            </>
          )}
          <select
            value={dateMode}
            onChange={(e) => {
              setDateMode(e.target.value);
              if (e.target.value !== 'custom') {
                setStartDate(''); setEndDate('');
                setPendingStart(''); setPendingEnd('');
              }
            }}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="admin-date-mode"
          >
            <option value="all">All</option>
            <option value="week">Week</option>
            <option value="monthly">Monthly</option>
            <option value="custom">Custom</option>
          </select>
          <button onClick={fetchData} className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-gray-100 rounded" title="Refresh" data-testid="admin-refresh">
            <RefreshCw size={14}/>
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 bg-white border border-gray-200 rounded-xl p-3">
        <FilterMultiSelect label="Country" options={data?.filters?.countries || []} value={countries} onChange={setCountries} testid="admin-filter-country" headerCount={hc.country}/>
        <FilterMultiSelect label="State" options={data?.filters?.states || []} value={states} onChange={setStates} testid="admin-filter-state" headerCount={hc.state}/>
        <FilterMultiSelect label="City" options={data?.filters?.cities || []} value={cities} onChange={setCities} testid="admin-filter-city" headerCount={hc.city}/>
        <FilterMultiSelect label="Rate Range" options={data?.filters?.rate_ranges || []} value={rateRanges} onChange={setRateRanges} testid="admin-filter-rate" headerCount={hc.rate_range}/>
        <FilterMultiSelect label="Room Type" options={data?.filters?.room_types || []} value={roomTypes} onChange={setRoomTypes} testid="admin-filter-type" headerCount={hc.room_type}/>
        <FilterMultiSelect label="No. of Rooms" options={data?.filters?.room_counts || []} value={roomCounts} onChange={setRoomCounts} testid="admin-filter-count" headerCount={hc.room_count}/>
      </div>

      {/* Selected chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="admin-selected-chips">
          <span className="text-xs text-gray-500">Selected:</span>
          {chips.map((c, i) => (
            <span key={`${c.key}-${c.label}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-teal-50 text-teal-700 border border-teal-200">
              {c.label}
              <button onClick={c.clear} className="hover:text-red-600"><X size={11}/></button>
            </span>
          ))}
          <button onClick={clearAll} className="text-xs text-teal-600 hover:underline ml-2" data-testid="admin-clear-all">Clear All</button>
        </div>
      )}

      {/* Total */}
      <div className="text-sm text-gray-700" data-testid="admin-total-properties">
        Total Properties: <span className="font-bold text-gray-900">{(data?.total_properties ?? 0).toLocaleString()}</span>
        <span className="text-gray-400 ml-3 text-xs">({(data?.total_rows ?? 0).toLocaleString()} room rows)</span>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {[
                  { key: 'property_name', label: 'Property Name' },
                  { key: 'state', label: 'State' },
                  { key: 'city', label: 'City' },
                  { key: 'room_type', label: 'Room Type' },
                  { key: 'room_rate', label: 'Room Rate' },
                  { key: 'room_count', label: 'No. of Rooms' },
                ].map(h => (
                  <th key={h.key} className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-3 py-12 text-center text-gray-400"><Loader2 className="animate-spin inline mr-2" size={14}/> Loading…</td></tr>
              )}
              {!loading && (!data || data.data.length === 0) && (
                <tr><td colSpan={6} className="px-3 py-12 text-center text-gray-400">No properties match the current filters.</td></tr>
              )}
              {!loading && data?.data.map((r, i) => (
                <tr key={`${r.property_id}-${r.room_type}-${i}`} className="odd:bg-white even:bg-gray-50 border-t border-gray-100" data-testid={`admin-row-${r.property_id}-${i}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-800 font-medium">{r.property_name || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.state || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{r.city || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">{r.room_type || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700 tabular-nums">{fmtRate(r.room_rate)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700 tabular-nums">{r.room_count ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.total_rows > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Showing {(data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total_rows)} of {data.total_rows.toLocaleString()} rows • {data.total_properties.toLocaleString()} unique properties</span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50 flex items-center gap-1"
              data-testid="admin-prev"><ChevronLeft size={12}/> Prev</button>
            <span className="px-2">Page {data.page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50 flex items-center gap-1"
              data-testid="admin-next">Next <ChevronRight size={12}/></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
